// cSpell:enableCompoundWords
import { IGameStats } from '@shared/types';
import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { useAppSelector } from 'app/hooks';
import Rank from 'app/scenes/statsmanager/components/rank/Rank';
import PopupContent from 'app/scenes/statsmanager/components/team/components/teamstat/popupcontent';
import { getAllTeamsGamesPlayedBySelectedWeek } from 'app/slices/LeagueSlice';
import Popup from 'components/popup/Popup';
import { getFormatUnitByCategory } from 'helpers/RankingsHelper';
import useResultCalculator from 'hooks/useResultCalculator';
import React, { ReactNode, useEffect, useState } from 'react';
import { FormatUnitType, IGameInfo, ITeamWeekRank, Maybe, TeamRankInfo } from 'types';

import styles from './TeamStat.module.scss';

type TeamStatProps = {
  className?: string;
  category: ITeamWeekRank;
  legend?: 'team' | 'week';
  icon?: ReactNode;
  showIcon?: boolean;
  showBye?: boolean;
  bestAvg?: number;
  worstAvg?: number;
  divisor?: number;
  popupEnabled?: boolean;
};

const CONSOLE_ON = getConsoleState(ConsoleID.TeamStat);

const TeamStat: React.FC<TeamStatProps> = ({
  className = '',
  category,
  legend = 'week',
  showIcon = false,
  showBye = false,
  bestAvg,
  worstAvg,
  icon,
  divisor = 1,
  popupEnabled = false,
}) => {
  const gamesPlayedUpToSelectedWeek = useAppSelector(getAllTeamsGamesPlayedBySelectedWeek);

  const [rankInfo, setRankInfo] = useState<TeamRankInfo>();
  const [legendStr, setLegendStr] = useState<string>('');

  const { getGameResult, getGameStats, getGameInfo } = useResultCalculator();
  const [gameResultCls, setGameResultCls] = useState<string>('');
  const [gameStats, setGameStats] = useState<IGameStats>();
  const [gameInfo, setGameInfo] = useState<Maybe<IGameInfo>>();

  const getAverageValue = (value: number, gamesPlayed: number, formatUnit: FormatUnitType = 'units') => {
    if (gamesPlayed === 0 || value === 0) return 0;
    if (CONSOLE_ON) console.log('getAverageValue', value, gamesPlayed, formatUnit);
    switch (formatUnit) {
      case 'time':
        return Math.round(value / gamesPlayed);
      case 'ratio':
        return value;
      case 'percent':
        return value;
      default:
        return Math.round((value / gamesPlayed) * 100) / 100;
    }
  };

  useEffect(() => {
    if (category && gamesPlayedUpToSelectedWeek) {
      setLegendStr(legend === 'team' ? category.teamName : category.week.replace('Week', 'Week '));
      const gamesPlayed = gamesPlayedUpToSelectedWeek[parseInt(category.teamID) - 1];

      if (CONSOLE_ON) console.log('getAverageValue', category.value, gamesPlayed);
      // const timeAverage = category.formatUnit === 'time' ? Math.round(category.value / gamesPlayed) : 0;
      // const perUnitAverage = category.formatUnit === 'ratio' ? category.value : 0;
      // const averageRounded = gamesPlayed !== 0 ? Math.round((category.value / gamesPlayed) * 100) / 100 : 0;
      // const avg = category.formatUnit !== 'percent' && category.formatUnit !== 'time' ? averageRounded : category.value;
      const tri: TeamRankInfo = {
        teamID: category.teamID,
        teamRank: category.rank,
        teamValue: category.value,
        teamAvg: getAverageValue(category.value, gamesPlayed, category.formatUnit),
        bestTeam: category.bestTeam,
        worstTeam: category.worstTeam,
        isRankTied: category.isRankTied,
        highOrLow: category.highOrLow,
        bestAvg: bestAvg,
        worstAvg: worstAvg,
        valueDivisor: divisor,
      };
      setRankInfo(tri);

      if (legend === 'week') {
        const result = getGameResult(category.week, category.teamID);
        setGameResultCls(styles[result]);
      }
    }
  }, [category, legend, getGameResult, bestAvg, worstAvg, divisor, gamesPlayedUpToSelectedWeek]);

  useEffect(() => {
    const gmStats: Maybe<IGameStats> = getGameStats(category.week, category.teamID);
    setGameInfo(getGameInfo(category.week, category.teamID));
    setGameStats(gmStats);
  }, [getGameStats, category.week, category.teamID]);

  return (
    <>
      {rankInfo && (
        <div className={styles.TeamStat}>
          {!showBye && (
            <Rank
              orientation="Vertical"
              className={className}
              rankInfo={rankInfo}
              displayType="HomeTeam"
              showAvg={!!rankInfo.valueDivisor && rankInfo.valueDivisor > 1 && !!rankInfo.teamAvg} // TODO FIX ME
              // chartBy="Value"
              chartBy="Rank"
              formatUnitType={getFormatUnitByCategory(category.rankCategory) ?? category.formatUnit ?? 'plays'}
              showUnitType={false}
            />
          )}
          <Popup
            disabled={!popupEnabled}
            popupContent={
              <PopupContent disabled={!popupEnabled} gameStats={gameStats} gameInfo={gameInfo} isByeWeek={showBye} />
            }
          >
            <span className={`${styles.WeekElement} ${gameResultCls}`}>{legendStr}</span>
            {showIcon && icon && <div className={styles.TeamStatOpponentLogo}>{icon}</div>}
            {showBye && <div className={styles.TeamStatBye}>BYE</div>}
          </Popup>
        </div>
      )}
    </>
  );
};

export default TeamStat;
